import {
  BadgeCheck,
  BarChart3,
  Check,
  CheckCheck,
  CircleAlert,
  Circle,
  CircleCheckBig,
  FileText,
  Forward,
  HardHat,
  KeyRound,
  Lock,
  Mail,
  Megaphone,
  MessagesSquare,
  NotebookText,
  Pencil,
  Plus,
  ReceiptText,
  Reply,
  RotateCcw,
  Rocket,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  ShieldX,
  Clock,
  SquareTerminal,
  Undo2,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { AppError } from '../../api/errors';
import type { Actor, AdPlatform, AutonomyKind, CheckVerdict, EventKind, Item, ItemEvent, ItemPayload, ItemType, WorkKind } from '../../domain/models';
import { approvedAutomatically, editedByPerson, eventRevision, handedTo, isTask, letInAfterCheck } from '../../domain/models';
import { isForGroup } from '../../domain/payload';
import type { Locale } from '../../l10n/messages';
import type { Translate } from '../../l10n/i18n';

// How domain values read on screen, ported from the mobile app's presentation.dart. Kept in one place
// so every screen says the same thing.

export function timeAgo(t: Translate, at: Date, now = new Date()): string {
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000);
  if (minutes < 1) return t('timeJustNow');
  if (minutes < 60) return t('timeMinutes', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('timeHours', { n: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('timeYesterday');
  return t('timeDays', { n: days });
}

/** A name in the dative, for sentences like "Perduoti Domui". Only Lithuanian declines. */
export const nameDative = (locale: Locale, name: string) => (locale === 'lt' ? dativeLt(name) : name);

export function dativeLt(name: string): string {
  if (name.includes(' ')) return name;
  const swap = (cut: number, end: string) => name.slice(0, name.length - cut) + end;
  if (name.endsWith('tis')) return swap(3, 'čiui');
  if (name.endsWith('dis')) return swap(3, 'džiui');
  if (name.endsWith('is') || name.endsWith('ys')) return swap(2, 'iui');
  if (name.endsWith('as') || name.endsWith('us')) return swap(2, 'ui');
  if (name.endsWith('ė')) return swap(1, 'ei');
  if (name.endsWith('a')) return swap(1, 'ai');
  return name;
}

export function channelName(channel: string): string {
  const names: Record<string, string> = {
    facebook: 'Facebook',
    facebook_messenger: 'Messenger',
    instagram: 'Instagram',
    reddit: 'Reddit',
    linkedin: 'LinkedIn',
    x: 'X',
    threads: 'Threads',
    email: 'El. paštas',
  };
  return names[channel] ?? channel;
}

const kindLabels: Record<WorkKind, string> = {
  request: 'typeScreening',
  report: 'typeReport',
  task: 'typeTask',
  offer: 'typeOffer',
  deliverable: 'typeDeliverable',
  access_request: 'typeAccessRequest',
  reply: 'typeReply',
  message: 'typeMessage',
  post: 'typePost',
  group_post: 'typeGroupPost',
  ad: 'typeAd',
  document: 'typeDocument',
  other: 'typeOther',
};

/** What kind of work this is, in a word: "Nauja užklausa", "Pasiūlymas", "Įrašas · Facebook". */
export function itemKindLabel(t: Translate, item: Pick<Item, 'kind' | 'payload'>): string {
  const label = t(kindLabels[item.kind]);
  return item.kind === 'post' && item.payload.type === 'social_post'
    ? `${label} · ${channelName(item.payload.channel)}`
    : label;
}

/** The kind of a task's source item, which travels as the API names its parts. */
export function sourceKind(type: string, payload: ItemPayload): WorkKind {
  const t = type as ItemType;
  if (t === 'screening') return 'request';
  if (t === 'report') return 'report';
  if (t === 'task') return 'task';
  switch (payload.type) {
    case 'offer':
      return 'offer';
    case 'deliverable':
      return 'deliverable';
    case 'access_request':
      return 'access_request';
    case 'message_reply':
      return payload.inReplyTo ? 'reply' : 'message';
    case 'social_post':
      return isForGroup(payload) ? 'group_post' : 'post';
    case 'ad_change':
      return 'ad';
    case 'document':
      return 'document';
    default:
      return 'other';
  }
}

const kindIcons: Record<WorkKind, LucideIcon> = {
  request: Mail,
  report: BarChart3,
  task: HardHat,
  offer: ReceiptText,
  deliverable: Rocket,
  access_request: KeyRound,
  reply: Reply,
  message: MessagesSquare,
  post: Megaphone,
  group_post: Users,
  ad: Megaphone,
  document: FileText,
  other: CircleCheckBig,
};

export const workKindIcon = (kind: WorkKind): LucideIcon => kindIcons[kind];

/** A kind of work as a group, without a count: "Pasiūlymai", "Įrašai grupėms". */
export function workKindName(t: Translate, kind: AutonomyKind): string {
  switch (kind) {
    case 'request':
      return t('workKindRequest');
    case 'access_request':
      return t('workKindAccessRequest');
    case 'offer':
      return t('workKindOffer');
    case 'deliverable':
      return t('workKindDeliverable');
    case 'reply':
      return t('workKindReply');
    case 'message':
      return t('workKindMessage');
    case 'post':
      return t('workKindPost');
    case 'group_post':
      return t('workKindGroupPost');
    case 'report':
      return t('workKindReport');
    case 'ad':
      return t('workKindAd');
    case 'document':
      return t('workKindDocument');
    case 'other':
      return t('workKindOther');
  }
}

const kindCounts: Record<WorkKind, string> = {
  request: 'kindRequests',
  report: 'kindReports',
  task: 'kindTasks',
  offer: 'kindOffers',
  deliverable: 'kindDeliverables',
  access_request: 'kindAccessRequests',
  reply: 'kindReplies',
  message: 'kindMessages',
  post: 'kindPosts',
  group_post: 'kindGroupPosts',
  ad: 'kindAds',
  document: 'kindDocuments',
  other: 'kindOther',
};

/** A count of one kind of work, for a folded list: "6 žinutės", "4 įrašai · Facebook". */
export function workKindCount(t: Translate, kind: WorkKind, count: number, channel?: string): string {
  const label = t(kindCounts[kind], { count });
  return kind === 'post' && channel ? `${label} · ${channelName(channel)}` : label;
}

/** What kind of secret an access request is about: "API raktas", "Prisijungimas", … */
export function accessKind(t: Translate, kind?: string): { label: string; icon: LucideIcon } {
  switch (kind) {
    case 'api_key':
      return { label: t('accessKindApiKey'), icon: KeyRound };
    case 'credentials':
      return { label: t('accessKindCredentials'), icon: Lock };
    case 'file':
      return { label: t('accessKindFile'), icon: FileText };
    case 'text':
      return { label: t('accessKindText'), icon: NotebookText };
    case 'ssh':
      return { label: 'SSH', icon: SquareTerminal };
    default:
      return { label: t('accessKindOther'), icon: KeyRound };
  }
}

export type Tone = 'accent' | 'warning' | 'success' | 'muted' | 'danger';

/** Foreground and background CSS colors for a tone. */
export const toneColors = (tone: Tone) =>
  ({
    accent: { fg: 'var(--accent)', bg: 'var(--accent-soft)' },
    warning: { fg: 'var(--warning)', bg: 'var(--warning-soft)' },
    success: { fg: 'var(--success)', bg: 'var(--success-soft)' },
    muted: { fg: 'var(--ink-muted)', bg: 'var(--sunken)' },
    danger: { fg: 'var(--danger)', bg: 'var(--danger-soft)' },
  })[tone];

export function statusStyle(t: Translate, item: Item): { label: string; tone: Tone } {
  switch (item.status) {
    case 'pending':
      return { label: t('statusPending'), tone: 'accent' };
    case 'changes_requested':
      return { label: t('statusChangesRequested'), tone: 'warning' };
    case 'approved':
      if (isTask(item)) return { label: t('statusWorking'), tone: 'accent' };
      if (item.execution === 'manual') {
        return {
          label: item.payload.type === 'message_reply' ? t('statusApprovedManualMessage') : t('statusApprovedManual'),
          tone: 'warning',
        };
      }
      if (item.execution === 'source') return { label: t('statusApprovedSource'), tone: 'success' };
      return { label: t('statusApproved'), tone: 'success' };
    case 'completed':
      return { label: t('statusCompleted'), tone: 'success' };
    case 'rejected':
      return { label: t('statusRejected'), tone: 'muted' };
    case 'failed':
      return { label: t('statusFailed'), tone: 'danger' };
    case 'withdrawn':
      return { label: t('statusWithdrawn'), tone: 'muted' };
    case 'expired':
      return { label: t('statusExpired'), tone: 'muted' };
    default:
      return { label: '?', tone: 'muted' };
  }
}

/** The isolated check's verdict in a word, with its tone and icon (ADR 0011). */
export function checkStyle(t: Translate, verdict: CheckVerdict): { label: string; tone: Tone; icon: LucideIcon } {
  switch (verdict) {
    case 'ok':
      return { label: t('checkOk'), tone: 'success', icon: ShieldCheck };
    case 'spam':
      return { label: t('checkSpam'), tone: 'warning', icon: ShieldAlert };
    case 'injection':
      return { label: t('checkInjection'), tone: 'danger', icon: ShieldX };
    case 'abuse':
      return { label: t('checkAbuse'), tone: 'danger', icon: ShieldX };
    default:
      return { label: t('checkUnclear'), tone: 'warning', icon: ShieldQuestion };
  }
}

export const actorName = (t: Translate, a: Actor) => (a.type === 'system' ? t('systemActor') : a.name);

export function eventLabel(t: Translate, locale: Locale, e: ItemEvent): string {
  const who = actorName(t, e.actor);
  switch (e.kind) {
    case 'created':
      return t('eventCreated', { actor: who });
    case 'revised':
      return editedByPerson(e) ? t('eventEdited', { actor: who }) : t('eventRevised', { actor: who, n: eventRevision(e) ?? 2 });
    case 'approved':
      if (letInAfterCheck(e)) return t('eventLetInAuto');
      return approvedAutomatically(e) ? t('eventApprovedAuto') : t('eventApproved', { actor: who });
    case 'rejected':
      return t('eventRejected', { actor: who });
    case 'changes_requested':
      return t('eventChangesRequested', { actor: who });
    case 'completed':
      return t('eventCompleted', { actor: who });
    case 'failed':
      return t('eventFailed', { actor: who });
    case 'withdrawn':
      return t('eventWithdrawn', { actor: who });
    case 'expired':
      return t('eventExpired');
    case 'handed_off':
      return t('eventHandedOff', { employee: nameDative(locale, handedTo(e) ?? '') });
    default:
      return t('eventUnknown', { actor: who });
  }
}

export function eventStyle(e: ItemEvent): { icon: LucideIcon; tone: Tone } {
  if (e.kind === 'approved' && approvedAutomatically(e)) return { icon: BadgeCheck, tone: 'success' };
  const styles: Record<EventKind, { icon: LucideIcon; tone: Tone }> = {
    created: { icon: Plus, tone: 'accent' },
    revised: { icon: Pencil, tone: 'accent' },
    approved: { icon: Check, tone: 'success' },
    completed: { icon: CheckCheck, tone: 'success' },
    rejected: { icon: X, tone: 'muted' },
    changes_requested: { icon: RotateCcw, tone: 'warning' },
    failed: { icon: CircleAlert, tone: 'danger' },
    withdrawn: { icon: Undo2, tone: 'muted' },
    expired: { icon: Clock, tone: 'muted' },
    handed_off: { icon: Forward, tone: 'accent' },
    unknown: { icon: Circle, tone: 'muted' },
  };
  return styles[e.kind];
}

/** A sentence for a failure. `employee` names who changed the item, for stale versions. */
export function errorMessage(t: Translate, e: AppError, employee?: string): string {
  switch (e.kind) {
    case 'network':
      return t('errorNetwork');
    case 'stale':
      return t('errorStale', { name: employee ?? 'AI' });
    case 'conflict':
    case 'alreadyHandedOff':
      return t('errorConflict');
    case 'forbidden':
      return t('errorForbidden');
    case 'notFound':
      return t('errorNotFound');
    case 'notAllowed':
      return t('errorNotAllowed');
    case 'invalidEmployee':
      return t('errorInvalidEmployee');
    case 'validation':
      return e.detail ?? t('errorValidation');
    case 'rateLimited':
      return t('errorRateLimited');
    case 'unauthorized':
      return t('errorSessionExpired');
    case 'invalidCredentials':
      return t('errorInvalidCredentials');
    case 'cancelled':
      return t('errorBiometricDenied');
    default:
      return t('errorGeneric');
  }
}

/** An amount in its currency, as the language writes it ("1 200 €", "€1,200"); whole amounts without
 * cents. A currency code Intl does not know falls back to `formatMoney`. */
export function formatCurrency(locale: Locale, amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return formatMoney(amount, currency);
  }
}

/** Where an ad runs, by name. */
export function adPlatformName(t: Translate, platform: AdPlatform): string {
  const names: Record<Exclude<AdPlatform, 'other'>, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    google: 'Google',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
  };
  return platform === 'other' ? t('adPlatformOther') : names[platform];
}

export function formatMoney(amount: number, currency: string): string {
  const n = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
  const spaced = n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
  return `${spaced} ${currency === 'EUR' ? '€' : currency}`;
}

/** The page color tinted with an employee's color, for headers that belong to them. */
export const headerTint = (color: string) => `color-mix(in srgb, ${color} 14%, var(--canvas))`;

